"use client";

import Link from "next/link";
import Image from "next/image";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion } from "framer-motion";
import { Mail, ArrowRight, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { forgotPassword } from "@/lib/api";
import { useHomeHref } from "@/hooks/useHomeHref";

const schema = z.object({
  email: z.string().min(1, "Informe o e-mail").email("E-mail inválido"),
});

type FormData = z.infer<typeof schema>;

export default function ForgotPasswordPage() {
  const { error: toastError } = useToast();
  const [sent, setSent] = useState(false);
  const [sentTo, setSentTo] = useState("");
  const homeHref = useHomeHref();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    const res = await forgotPassword(data.email.trim().toLowerCase());
    if (!res.success) {
      toastError("Não foi possível enviar", res.error.message);
      return;
    }
    setSentTo(data.email);
    setSent(true);
  };

  return (
    <div className="min-h-[calc(100vh-64px)] flex flex-col items-center justify-center px-4 py-12 bg-[#FFF8FA]">
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        className="w-full max-w-sm"
      >
        <Link href={homeHref} aria-label="ILovePage — início" className="inline-flex mb-8">
          <Image
            src="/brand/logo-horizontal.png"
            alt="ILovePage"
            width={2172}
            height={724}
            priority
            className="h-12 w-auto"
          />
        </Link>

        {sent ? (
          <div className="text-center">
            <motion.div
              initial={{ scale: 0.85 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 18 }}
              className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-green-50 mb-5"
            >
              <CheckCircle2 className="h-8 w-8 text-green-500" />
            </motion.div>
            <h1 className="text-2xl font-black text-[#1F2937] mb-2">
              Olha o seu e-mail!
            </h1>
            <p className="text-gray-500 text-sm leading-relaxed mb-6">
              Se{" "}
              <strong className="text-[#1F2937]">{sentTo}</strong>{" "}
              estiver cadastrado, enviamos um link para criar uma senha nova.
              O link vale por 1 hora.
            </p>
            <p className="text-xs text-gray-400 mb-8 leading-relaxed">
              Não chegou? Confira a caixa de spam ou volte e tente de novo.
            </p>
            <div className="flex flex-col gap-3">
              <Button asChild>
                <Link href="/login">
                  Voltar para o login
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button variant="ghost" onClick={() => setSent(false)}>
                Reenviar para outro e-mail
              </Button>
            </div>
          </div>
        ) : (
          <>
            <h1 className="text-3xl font-black text-[#1F2937] mb-2">
              Esqueceu a senha?
            </h1>
            <p className="text-gray-500 text-sm mb-8 leading-relaxed">
              Informe o e-mail da sua conta. Vamos enviar um link para criar
              uma senha nova — leva 1 minuto.
            </p>

            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="email">E-mail</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com.br"
                    className="pl-10"
                    autoFocus
                    autoComplete="email"
                    error={errors.email?.message}
                    {...register("email")}
                  />
                </div>
              </div>

              <Button type="submit" size="lg" loading={isSubmitting}>
                {isSubmitting ? "Enviando..." : "Enviar link de redefinição"}
                {!isSubmitting && <ArrowRight className="h-4 w-4" />}
              </Button>
            </form>

            <p className="text-sm text-gray-500 text-center mt-8">
              Lembrou da senha?{" "}
              <Link
                href="/login"
                className="text-[#E11D48] font-semibold hover:underline"
              >
                Voltar para o login
              </Link>
            </p>
          </>
        )}
      </motion.div>
    </div>
  );
}
